import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  SpaceBetween, Box, Button, Textarea, Container, Header,
} from "@cloudscape-design/components";
import { Comment, UserInfo } from "../types";
import { apiClient } from "../api/client";

interface Props {
  eventId: string;
  user: UserInfo;
  isAdmin: boolean;
}

export const CommentSection: React.FC<Props> = ({ eventId, user, isAdmin }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newText, setNewText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const [pendingImages, setPendingImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fetchComments = useCallback(async () => {
    try {
      const data = await apiClient.get(`/events/${eventId}/comments`);
      setComments(data.comments || []);
    } catch (err) {
      console.error("Failed to fetch comments:", err);
    }
  }, [eventId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  // Handle paste for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf("image") !== -1) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          if (blob) handleImageFile(blob);
        }
      }
    };

    const el = containerRef.current;
    if (el) el.addEventListener("paste", handlePaste);
    return () => {
      const el = containerRef.current;
      if (el) el.removeEventListener("paste", handlePaste);
    };
  }, []);

  const handleImageFile = async (file: File) => {
    try {
      setUploading(true);
      const imageUrl = await apiClient.uploadImage(file);
      setPendingImages(prev => [...prev, imageUrl]);
    } catch (err) {
      console.error("Failed to upload image:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(handleImageFile);
  };

  const handlePost = async () => {
    if (!newText.trim() && pendingImages.length === 0) return;
    try {
      await apiClient.post(`/events/${eventId}/comments`, {
        text: newText.trim(),
        images: pendingImages,
      });
      setNewText("");
      setPendingImages([]);
      fetchComments();
    } catch (err) {
      console.error("Failed to post comment:", err);
    }
  };

  const handleDelete = async (comment: Comment) => {
    try {
      await apiClient.delete(`/events/${eventId}/comments/${comment.commentId}`);
      fetchComments();
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  return (
    <Container header={<Header variant="h3">Comments ({comments.length})</Header>}>
      <SpaceBetween size="m">
        {/* Comment list */}
        {comments.length === 0 && (
          <Box variant="p" color="text-status-inactive">No comments yet.</Box>
        )}
        {comments.map(comment => (
          <div key={comment.commentId} style={{ display: "flex", gap: 12, padding: "12px", background: "#f8f8f8", borderRadius: 8 }}>
            <a href={`https://phonetool.amazon.com/users/${comment.authorAlias}`} target="_blank" rel="noreferrer">
              <img
                src={`https://badgephotos.corp.amazon.com/?login=${comment.authorAlias}`}
                alt={comment.authorAlias}
                style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#232F3E" }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </a>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box variant="strong">{comment.authorName} <span style={{ fontWeight: 400, color: "#888", fontSize: "0.8rem" }}>({comment.authorAlias})</span></Box>
                <SpaceBetween direction="horizontal" size="xxs">
                  <Box variant="small" color="text-status-inactive">
                    {new Date(comment.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </Box>
                  {(isAdmin || comment.authorAlias === user.alias) && (
                    <Button variant="icon" iconName="remove" onClick={() => handleDelete(comment)} />
                  )}
                </SpaceBetween>
              </div>
              {comment.text && <Box variant="p" margin={{ top: "xxs" }}>{comment.text}</Box>}
              {comment.images && comment.images.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {comment.images.map((img, idx) => (
                    <img key={idx} src={img} style={{ maxWidth: 200, maxHeight: 150, borderRadius: 4, cursor: "pointer" }} onClick={() => window.open(img)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add comment form */}
        <div ref={containerRef} style={{ borderTop: "1px solid #e7e7e7", paddingTop: 12 }}>
          <Box variant="small" color="text-status-inactive" margin={{ bottom: "xs" }}>
            Paste images with Ctrl+V or drag and drop
          </Box>
          <Textarea
            value={newText}
            onChange={({ detail }) => setNewText(detail.value)}
            placeholder="Add a comment..."
            rows={3}
          />
          {pendingImages.length > 0 && (
            <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              {pendingImages.map((img, idx) => (
                <img key={idx} src={img} style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 4 }} />
              ))}
            </div>
          )}
          <SpaceBetween direction="horizontal" size="xs" >
            <label style={{ cursor: "pointer", padding: "6px 12px", border: "1px solid #d5d9d9", borderRadius: 4, fontSize: "0.85rem", marginTop: 8, display: "inline-block" }}>
              Browse Images
              <input type="file" accept="image/*" multiple onChange={handleFileSelect} style={{ display: "none" }} />
            </label>
            <Button variant="primary" onClick={handlePost} loading={uploading} disabled={!newText.trim() && pendingImages.length === 0}>
              Post Comment
            </Button>
          </SpaceBetween>
        </div>
      </SpaceBetween>
    </Container>
  );
};
